import { useNavigation, useList, useCustom } from '@refinedev/core';
import { List, CreateButton } from '@refinedev/antd';
import { Table, Space, Tag, Button, Select, Segmented, DatePicker, Switch } from 'antd';
import {
  EyeOutlined,
  SwapOutlined,
} from '@ant-design/icons';
import { useState, useMemo } from 'react';
import dayjs, { Dayjs } from 'dayjs';

interface BusStationCity {
  id: number;
  nameUz: string;
  nameRu: string;
  nameEn: string;
  region: { id: number; nameUz: string; nameRu: string; nameEn: string };
}

interface BusStationItem {
  id: number;
  nameUz: string;
  city: BusStationCity;
}

interface BusStation {
  id: number;
  nameUz: string;
  nameRu: string;
  nameEn: string;
  address?: string;
}

interface BusTrip {
  id: number;
  busCompany: { id: number; name: string };
  departureCity: { nameUz: string; nameRu: string; nameEn: string };
  arrivalCity: { nameUz: string; nameRu: string; nameEn: string };
  departureRegion: { nameUz: string; nameRu: string; nameEn: string };
  arrivalRegion: { nameUz: string; nameRu: string; nameEn: string };
  departureBusStation: BusStation | string;
  arrivalBusStation: BusStation | string;
  departureTime: string;
  arrivalTime: string;
  pricePerSeat: number;
  totalSeats: number;
  busType: string;
  status: string;
  busNumber: string;
  amenities: string[];
  _count: { bookings: number };
  createdAt: string;
}

type DatePreset = 'BUGUN' | 'ERTAGA' | 'SANA' | 'ALL';

export const BusTripList = () => {
  const { show } = useNavigation();
  const [selectedDepartureCity, setSelectedDepartureCity] = useState<string | null>(null);
  const [selectedArrivalCity, setSelectedArrivalCity] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string | undefined>();
  // Date filter: defaults to "Bugun" so operators see today's reys first.
  const [datePreset, setDatePreset] = useState<DatePreset>('BUGUN');
  const [customDate, setCustomDate] = useState<Dayjs | null>(dayjs());
  // Hide past trips by default. Toggleable so admin can audit history.
  const [showPast, setShowPast] = useState(false);

  // Fetch bus stations to get available cities
  const { data: stationsData } = useCustom<BusStationItem[]>({
    url: '/admin/bus/stations',
    method: 'get',
  });

  const resolvedDate = useMemo(() => {
    if (datePreset === 'BUGUN') return dayjs().startOf('day');
    if (datePreset === 'ERTAGA') return dayjs().add(1, 'day').startOf('day');
    if (datePreset === 'SANA' && customDate) return customDate.startOf('day');
    return null;
  }, [datePreset, customDate]);

  // Hit /admin/bus/trips/by-date when a specific day is selected. Falls back
  // to the regular paginated list when "ALL" is chosen.
  const useByDate = !!resolvedDate;
  const { data: byDateData, isLoading: byDateLoading } = useCustom<BusTrip[]>({
    url: '/admin/bus/trips/by-date',
    method: 'get',
    config: { query: { date: resolvedDate?.format('YYYY-MM-DD') } },
    queryOptions: { enabled: useByDate },
  });

  const { data: listData, isLoading: listLoading } = useList<BusTrip>({
    resource: 'admin/bus/trips',
    pagination: { pageSize: 20 },
    filters: statusFilter
      ? [{ field: 'status', operator: 'eq' as const, value: statusFilter }]
      : [],
    queryOptions: { enabled: !useByDate },
  });

  const isLoading = useByDate ? byDateLoading : listLoading;
  const data = useByDate ? byDateData : listData;
  const rawTrips = (data?.data as BusTrip[] | undefined) || [];
  const trips = showPast
    ? rawTrips
    : rawTrips.filter((t) => new Date(t.departureTime) >= new Date(Date.now() - 60 * 60 * 1000));

  // Extract unique cities from bus stations
  const cityOptions = useMemo(() => {
    const raw = stationsData?.data;
    const stations: BusStationItem[] = Array.isArray(raw)
      ? raw
      : (raw as any)?.data || [];
    const cities = new Map<string, string>();
    stations.forEach((station) => {
      const cityName = station.city?.nameUz;
      const regionName = station.city?.region?.nameUz;
      if (cityName && !cities.has(cityName)) {
        cities.set(cityName, regionName || '');
      }
    });
    return Array.from(cities.entries())
      .map(([name, region]) => ({
        value: name,
        label: region ? `${name} (${region})` : name,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [stationsData]);

  // Filter trips based on status + selected cities. Status is applied here
  // (client-side) as well as server-side, because the "by-date" endpoint used
  // for date presets ignores the status query param — without this the status
  // Select silently did nothing whenever a date preset (incl. the default
  // "Bugun") was active.
  const filteredTrips = useMemo(() => {
    return trips.filter((trip) => {
      if (statusFilter && trip.status !== statusFilter) return false;
      if (selectedDepartureCity && trip.departureCity?.nameUz !== selectedDepartureCity) return false;
      if (selectedArrivalCity && trip.arrivalCity?.nameUz !== selectedArrivalCity) return false;
      return true;
    });
  }, [trips, statusFilter, selectedDepartureCity, selectedArrivalCity]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('uz-UZ').format(value) + " so'm";
  };

  const statusColors: Record<string, string> = {
    SCHEDULED: 'blue',
    DEPARTED: 'orange',
    COMPLETED: 'green',
    CANCELLED: 'red',
  };

  const statusLabels: Record<string, string> = {
    SCHEDULED: 'Rejalashtirilgan',
    DEPARTED: "Yo'lda",
    COMPLETED: 'Yakunlangan',
    CANCELLED: 'Bekor qilingan',
  };

  const busTypeLabels: Record<string, string> = {
    STANDARD: 'Standart',
    VIP: 'VIP',
    SLEEPER: 'Sleeper',
  };

  const columns = [
    {
      title: 'Kompaniya',
      dataIndex: ['busCompany', 'name'],
      key: 'company',
    },
    {
      title: "Yo'nalish",
      key: 'route',
      render: (_: unknown, record: BusTrip) => {
        const departureStation =
          typeof record.departureBusStation === 'object'
            ? record.departureBusStation?.nameUz
            : record.departureBusStation;
        const arrivalStation =
          typeof record.arrivalBusStation === 'object'
            ? record.arrivalBusStation?.nameUz
            : record.arrivalBusStation;
        return (
          <div>
            <div style={{ fontWeight: 500 }}>
              {record.departureCity?.nameUz} → {record.arrivalCity?.nameUz}
            </div>
            <div style={{ color: '#7F8C8D', fontSize: 11 }}>
              {departureStation} → {arrivalStation}
            </div>
          </div>
        );
      },
    },
    {
      title: "Jo'nash vaqti",
      dataIndex: 'departureTime',
      key: 'departureTime',
      render: (date: string) =>
        new Date(date).toLocaleString('uz-UZ', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        }),
    },
    {
      title: "O'rindiqlar",
      key: 'seats',
      render: (_: unknown, record: BusTrip) => {
        const booked = record._count?.bookings ?? 0;
        const total = record.totalSeats;
        const percentage = total > 0 ? Math.round((booked / total) * 100) : 0;
        return (
          <span>
            {booked}/{total}{' '}
            <Tag color={percentage > 80 ? 'green' : percentage > 50 ? 'blue' : 'default'}>
              {percentage}%
            </Tag>
          </span>
        );
      },
    },
    {
      title: 'Narx',
      dataIndex: 'pricePerSeat',
      key: 'pricePerSeat',
      render: (price: number) => formatCurrency(price),
    },
    {
      title: 'Turi',
      dataIndex: 'busType',
      key: 'busType',
      render: (type: string) => (
        <Tag color={type === 'VIP' ? 'gold' : type === 'SLEEPER' ? 'purple' : 'default'}>
          {busTypeLabels[type] || type}
        </Tag>
      ),
    },
    {
      title: 'Holat',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={statusColors[status] || 'default'}>{statusLabels[status] || status}</Tag>
      ),
    },
    {
      title: 'Buyurtmalar',
      key: 'bookings',
      render: (_: unknown, record: BusTrip) => record._count?.bookings || 0,
    },
    {
      title: 'Amallar',
      key: 'actions',
      render: (_: unknown, record: BusTrip) => (
        <Button
          type="text"
          icon={<EyeOutlined />}
          onClick={() => show('admin/bus/trips', record.id)}
        />
      ),
    },
  ];

  const handleSwap = () => {
    setSelectedDepartureCity(selectedArrivalCity);
    setSelectedArrivalCity(selectedDepartureCity);
  };

  return (
    <List
      title="Avtobus reyslari"
      headerButtons={
        <Space>
          <Select
            placeholder="Holat"
            allowClear
            style={{ width: 160 }}
            onChange={(val) => setStatusFilter(val)}
            options={[
              { value: 'SCHEDULED', label: 'Rejalashtirilgan' },
              { value: 'DEPARTED', label: "Yo'lda" },
              { value: 'COMPLETED', label: 'Yakunlangan' },
              { value: 'CANCELLED', label: 'Bekor qilingan' },
            ]}
          />
          <CreateButton>Yangi reys</CreateButton>
        </Space>
      }
    >
      {/* Date filter — Bugun / Ertaga / Sana / Barchasi */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 12,
          marginBottom: 16,
          flexWrap: 'wrap',
        }}
      >
        <Segmented
          value={datePreset}
          onChange={(v) => setDatePreset(v as DatePreset)}
          options={[
            { label: 'Bugun', value: 'BUGUN' },
            { label: 'Ertaga', value: 'ERTAGA' },
            { label: 'Sana', value: 'SANA' },
            { label: 'Barchasi', value: 'ALL' },
          ]}
        />
        {datePreset === 'SANA' && (
          <DatePicker
            value={customDate}
            onChange={(d) => setCustomDate(d)}
            format="YYYY-MM-DD"
          />
        )}
        <Space>
          <Switch
            checked={showPast}
            onChange={setShowPast}
            size="small"
          />
          <span>O'tgan reyslarni ko'rsatish</span>
        </Space>
      </div>

      {/* City selection filters */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 12,
          marginBottom: 24,
        }}
      >
        <Select
          placeholder="Qayerdan (jo'nash shahri)"
          allowClear
          showSearch
          optionFilterProp="label"
          style={{ width: 280 }}
          size="large"
          value={selectedDepartureCity}
          onChange={(val) => setSelectedDepartureCity(val || null)}
          options={cityOptions}
        />
        <Button
          icon={<SwapOutlined />}
          shape="circle"
          size="large"
          onClick={handleSwap}
          disabled={!selectedDepartureCity && !selectedArrivalCity}
        />
        <Select
          placeholder="Qayerga (borish shahri)"
          allowClear
          showSearch
          optionFilterProp="label"
          style={{ width: 280 }}
          size="large"
          value={selectedArrivalCity}
          onChange={(val) => setSelectedArrivalCity(val || null)}
          options={cityOptions}
        />
      </div>

      <Table
        dataSource={filteredTrips}
        columns={columns}
        rowKey="id"
        loading={isLoading}
        pagination={{ pageSize: 20, showSizeChanger: true, showTotal: (total) => `Jami: ${total}` }}
        size="middle"
      />
    </List>
  );
};
